import type {
  FmNavGroupEntry,
  FmRouteCatalogData,
  FmRouteCatalogEntry,
  PrimaryNavId,
} from './FmRouteCatalog';

export function getGroupRoutes(
  catalog: FmRouteCatalogData,
  groupId: string,
): FmRouteCatalogEntry[] {
  const group = catalog.groups.find((candidate) => candidate.id === groupId);
  if (!group) return [];
  return group.routeIds
    .map((id) => catalog.routes.find((route) => route.id === id))
    .filter(
      (route): route is FmRouteCatalogEntry => (
        route !== undefined && route.placement === 'menu'
      ),
    );
}

export function getJobGroups(
  catalog: FmRouteCatalogData,
  jobId: PrimaryNavId,
): FmNavGroupEntry[] {
  const job = catalog.topJobs.find((candidate) => candidate.id === jobId);
  if (!job) return [];
  return job.groupIds
    .map((id) => catalog.groups.find((group) => group.id === id))
    .filter((group): group is FmNavGroupEntry => group !== undefined);
}

/**
 * Compile a parameterized catalog path into an exact matcher. Escape literal
 * regex characters before substituting `:param` so the inserted character
 * class is not mangled by the escape pass.
 */
export function routePathToRegExp(routePath: string): RegExp {
  const escaped = routePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = escaped.replace(/:[a-zA-Z_]\w*/g, '[^/]+');
  return new RegExp(`^${pattern}$`);
}

function routePathPrefix(routePath: string): string {
  const paramIdx = routePath.indexOf('/:');
  return paramIdx === -1 ? routePath : routePath.slice(0, paramIdx);
}

const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

export type RouteQueryContextResolution =
  | { kind: 'valid' }
  | { kind: 'normalize'; search: string }
  | { kind: 'missing-required' };

export function parseQueryPropertyContext(
  params: URLSearchParams,
  parameter = 'property',
): string | null {
  const values = params.getAll(parameter);
  if (values.length !== 1) return null;
  const value = values[0]?.trim();
  return value && !value.includes(',') ? value : null;
}

/**
 * Resolve the catalog's query-context contract once, before a route mounts.
 * Optional global routes shed property-dependent state when its owner forest
 * is absent; required routes enter their governed property chooser.
 */
export function resolveRouteQueryContext(
  route: FmRouteCatalogEntry,
  search = '',
): RouteQueryContextResolution {
  const context = route.propertyContext;
  if (!context) return { kind: 'valid' };

  const params = new URLSearchParams(search);
  const propertyValues = params.getAll(context.parameter);
  const propertyId = parseQueryPropertyContext(params, context.parameter);
  if (propertyId) {
    return { kind: 'valid' };
  }
  if (context.requirement === 'required') {
    return { kind: 'missing-required' };
  }

  const dependentParameters = context.requiredWithParameters ?? [];
  const dependentParameterValues = context.requiredWithParameterValues ?? {};
  const matchedValueParameters = Object.entries(dependentParameterValues)
    .filter(([parameter, values]) => (
      params.getAll(parameter).some((value) => values.includes(value))
    ));
  const needsNormalization = (
    propertyValues.length > 0
    || dependentParameters.some((parameter) => params.has(parameter))
    || matchedValueParameters.length > 0
  );
  if (!needsNormalization) {
    return { kind: 'valid' };
  }

  params.delete(context.parameter);
  dependentParameters.forEach((parameter) => params.delete(parameter));
  matchedValueParameters.forEach(([parameter, scopedValues]) => {
    const preservedValues = params.getAll(parameter).filter(
      (value) => !scopedValues.includes(value),
    );
    params.delete(parameter);
    preservedValues.forEach((value) => params.append(parameter, value));
  });
  const normalized = params.toString();
  return {
    kind: 'normalize',
    search: normalized ? `?${normalized}` : '',
  };
}

export function getRequiredCatalogRoute(
  catalog: FmRouteCatalogData,
  routeId: string,
): FmRouteCatalogEntry {
  const route = catalog.routes.find((candidate) => candidate.id === routeId);
  if (!route) {
    throw new Error(`Navigation route "${routeId}" is not registered.`);
  }
  return route;
}

export function getCatalogRouteFromPath(
  catalog: FmRouteCatalogData,
  pathname: string,
): FmRouteCatalogEntry | undefined {
  const path = stripTrailingSlash(pathname);

  for (const route of catalog.routes) {
    if (routePathToRegExp(route.path).test(path)) return route;
  }
  for (const route of catalog.routes) {
    if (route.ownsSubtree === false) continue;
    const prefix = routePathPrefix(route.path);
    if (prefix && (path === prefix || path.startsWith(`${prefix}/`))) return route;
  }
  return undefined;
}

export function getRoutePropertyId(
  route: FmRouteCatalogEntry,
  pathname: string,
  search = '',
): string | null {
  if (route.propertyContext?.source === 'query') {
    return parseQueryPropertyContext(
      new URLSearchParams(search),
      route.propertyContext.parameter,
    );
  }
  if (route.requiredContext !== 'property') return null;
  const propertyIndex = route.path.split('/').indexOf(':propertyId');
  if (propertyIndex === -1) return null;
  const encodedPropertyId = stripTrailingSlash(pathname).split('/')[propertyIndex];
  if (!encodedPropertyId) return null;
  try {
    return decodeURIComponent(encodedPropertyId);
  } catch {
    return null;
  }
}

export function getCatalogRoutePropertyId(
  catalog: FmRouteCatalogData,
  pathname: string,
  search = '',
): string | null {
  const route = getCatalogRouteFromPath(catalog, pathname);
  return route ? getRoutePropertyId(route, pathname, search) : null;
}

export function getPrimaryNavFromPath(
  catalog: FmRouteCatalogData,
  pathname: string,
): PrimaryNavId | undefined {
  return getCatalogRouteFromPath(catalog, pathname)?.primaryNavId;
}
